/**
 * Turns bare email addresses in an HTML string into `mailto:` links.
 *
 * Needs a DOM, so it must never be reachable from a render - the server has none and a throw there
 * costs the whole rendering its markup. Gate callers on `useIsMounted`.
 *
 * Everything that is not a matched address has to come through untouched, which is why the text
 * nodes are rebuilt as nodes rather than by assigning `innerHTML`: writing HTML back would re-parse
 * the surrounding text, and writing it to the *parent* would drop that parent's other children.
 */

/** Deliberately ends on a word character, so a sentence's full stop stays out of the address. */
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;

const getTextNodes = (element) => {
  const textNodes = [];
  const walk = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
  let node;

  while ((node = walk.nextNode())) {
    textNodes.push(node);
  }
  return textNodes;
};

/** The text with its addresses linked, as nodes - or null when it holds no address. */
const linkify = (text) => {
  const matches = [...text.matchAll(EMAIL)];
  if (!matches.length) {
    return null;
  }

  const fragment = document.createDocumentFragment();
  let position = 0;
  matches.forEach((match) => {
    if (match.index > position) {
      fragment.append(text.slice(position, match.index));
    }
    const link = document.createElement('a');
    link.href = `mailto:${match[0]}`;
    link.textContent = match[0];
    fragment.append(link);
    position = match.index + match[0].length;
  });
  if (position < text.length) {
    fragment.append(text.slice(position));
  }

  return fragment;
};

export default (html) => {
  const element = document.createElement('div');
  element.innerHTML = html;

  getTextNodes(element).forEach((textNode) => {
    if (textNode.parentNode.tagName === 'A') {
      return;
    }
    const linked = linkify(textNode.nodeValue);
    if (linked) {
      textNode.replaceWith(linked);
    }
  });

  const newHtml = String(element.innerHTML);
  element.remove();

  return newHtml;
};
